import type { SupervisorButton } from '../types/supervisorButtons';
import { getCreditShortfall } from '../utils/creditGate';
import { QuotaQueueInfo, quotaQueueInfo } from '../utils/quotaQueue';

import { errorMessage, normalizeApiError, responseError } from './apiError';
import axiosInstance from './axiosInstance';
import { signalInsufficientCredits } from './creditGateEvents';
import { eventService } from './eventService';
import { tokenService } from './tokenService';

export type DesignTier = 'basic' | 'extreme';

export interface ExtremeDesignInfo {
  eligible: boolean;
  required_plan: string;
  section_credit_multiplier: number;
  basic_section_credit: number;
  extreme_section_credit: number;
}

/**
 * Model the planning chat requests: Luna with reasoning off. The API runs the
 * DeepSeek Flash confidence probe first and escalates to this gateway group.
 */

export const DEFAULT_CHAT_MODEL = 'gpt-5.6-luna';

/**
 * Interface for chat message
 */
export interface ChatMessage {
  id: string;
  user_id: string;
  project_id?: string | null;
  session_id?: string | null;
  content: string;
  media_urls?: string[] | null;
  media_metadata?: Record<string, unknown>[] | null;
  prompt_scope?: Record<string, unknown> | null;
  author: string;
  sender_name?: string | null;
  sender_email?: string | null;
  created_at: string;
  updated_at: string;
  phase?: string | null;
  allowed_buttons?: string[] | null;
  buttons?: SupervisorButton[] | null;
  clicked_button?: string | null;
  clicked_buttons?: string[] | null;
  progress?: number | null;
  developer_status_steps?: DeveloperStatusStep[];
  developer_job_id?: string | null;
  developer_status?: string | null;
  /** The web searches the reply made, in order (stored with the message, streamed while it runs). */
  tool_activity?: WebSearchActivity[] | null;
}

export interface WebSearchSource {
  title: string;
  url: string;
}

/** One `web_search` tool call of a chat reply: the query the model searched, then its sources and charge. */
export interface WebSearchActivity {
  type: 'web_search';
  id: string;
  query: string;
  status: 'searching' | 'done' | 'failed' | 'insufficient_credits';
  sources: WebSearchSource[];
  /** Credits charged for the search (0 until it finished, or when it did not run). */
  credits: number;
}

export interface DeveloperStatusStep {
  id?: string;
  project_id?: string;
  session_id?: string | null;
  job_id?: string | null;
  step_key: string;
  label: string;
  status?: string | null;
  message?: string | null;
  reason?: string | null;
  detail?: string | null;
  progress?: number | null;
  index_mode?: string | null;
  changed_count?: number | null;
  deleted_count?: number | null;
  indexed_path_count?: number | null;
  order_index?: number | null;
  started_at?: string | null;
  ended_at?: string | null;
  duration_ms?: number | null;
}

/**
 * Interface for message list response
 */
interface MessageListResponse {
  messages: ChatMessage[];
  total: number;
}

interface MessageListOptions {
  projectId?: string;
  sessionId?: string | null;
}

/**
 * Interface for AI message response (contains both user and AI messages)
 */
interface AIMessageResponse {
  project_id: string;
  user_message: ChatMessage;
  ai_message: ChatMessage;
}

export interface TriggerAgentsResponse {
  status: 'triggered' | 'skipped';
  agents: string[];
  reason?: string;
}

interface StreamCallbacks {
  onUserMessageId?: (id: string, projectId?: string) => void;
  onAssistantMessageId?: (id: string) => void;
  onChunk: (content: string) => void;
  onComplete: (payload: {
    userMessageId: string;
    aiMessageId: string;
    projectId?: string;
    sessionId?: string | null;
  }) => void;
  onError: (error: string) => void;
  onWarning?: (message: string) => void;
  /** The reply started or finished a web search (the same id arrives for both). */
  onWebSearch?: (activity: WebSearchActivity) => void;
  /** The API queued this message for the plan's hourly quota instead of answering it now. */
  onQuotaQueued?: (info: QuotaQueueInfo) => void;
  onStreamEnd?: () => void;
  onSuggestedAgents?: (
    agents: string[],
    buttons: SupervisorButton[],
    projectId: string,
    messageId: string,
    sessionId?: string | null,
    promptScope?: Record<string, unknown> | null,
    aiContent?: string | null
  ) => void;
}

interface StreamState {
  userMessageId: string;
  resolvedProjectId: string | undefined;
}

export interface SelectedDesignElement {
  page_id: string;
  node_id: string;
  instance_index: number;
  base_version: number;
}

type StreamEventData = Record<string, unknown>;

function handleUserMessage(data: StreamEventData, state: StreamState, callbacks: StreamCallbacks) {
  state.userMessageId = data.id as string;
  state.resolvedProjectId = (data.project_id as string) || state.resolvedProjectId;
  const promptScope = data.prompt_scope as Record<string, unknown> | undefined;

  if (promptScope?.action === 'revise_component' && data.content && data.created_at) {
    eventService.emit('chat_message', {
      ...data,
      type: 'chat_message',
      author: 'user',
    });
  }
  callbacks.onUserMessageId?.(state.userMessageId, state.resolvedProjectId);
}

function handleToken(data: StreamEventData, callbacks: StreamCallbacks) {
  if (typeof data.ai_message_id === 'string' && data.ai_message_id) {
    callbacks.onAssistantMessageId?.(data.ai_message_id);
  }
  if (data.content) {
    callbacks.onChunk(data.content as string);
  }
}

function handleDone(data: StreamEventData, state: StreamState, callbacks: StreamCallbacks) {
  state.resolvedProjectId = (data.project_id as string) || state.resolvedProjectId;
  if (typeof data.ai_message_id === 'string' && data.ai_message_id) {
    callbacks.onAssistantMessageId?.(data.ai_message_id);
  }
  callbacks.onComplete({
    userMessageId: state.userMessageId,
    aiMessageId: data.ai_message_id as string,
    projectId: state.resolvedProjectId,
    sessionId: (data.session_id as string) || null,
  });
}

function processStreamEvent(data: StreamEventData, state: StreamState, callbacks: StreamCallbacks) {
  switch (data.type) {
  case 'user_message':
    handleUserMessage(data, state, callbacks);
    break;
  case 'token':
    handleToken(data, callbacks);
    break;
  case 'web_search':
    callbacks.onWebSearch?.(data as unknown as WebSearchActivity);
    break;
  case 'done':
    handleDone(data, state, callbacks);
    break;
  case 'error': {
    const streamError = data.error as { message?: string } | undefined;

    callbacks.onError(streamError?.message || (data.message as string) || 'The AI response stopped unexpectedly. Try again.');
    break;
  }
  case 'supervisor_skipped':
    if (callbacks.onWarning) {
      callbacks.onWarning((data.message as string) || 'Planning agent needs at least 10 credits');
    }
    break;
  case 'already_has_assistant':
    if (typeof data.ai_message_id === 'string' && data.ai_message_id) {
      callbacks.onAssistantMessageId?.(data.ai_message_id);
    }
    callbacks.onComplete({
      userMessageId: state.userMessageId,
      aiMessageId: data.ai_message_id as string,
      projectId: state.resolvedProjectId,
      sessionId: (data.session_id as string) || null,
    });
    break;
  case 'suggested_agents':
    if (callbacks.onSuggestedAgents) {
      callbacks.onSuggestedAgents(
        (data.agents as string[]) || [],
        (data.buttons as SupervisorButton[]) || [],
        (data.project_id as string) || '',
        (data.message_id as string) || '',
        (data.session_id as string) || null,
        (data.prompt_scope as Record<string, unknown>) || null,
        (data.ai_content as string) || null,
      );
    }
    break;
  }
}

function parseDataLine(part: string, state: StreamState, callbacks: StreamCallbacks): void {
  if (!part.startsWith('data: ')) return;

  try {
    const data = JSON.parse(part.slice(6));

    processStreamEvent(data, state, callbacks);
  } catch (err) {
    callbacks.onError(err instanceof Error ? err.message : 'Stream parsing error');
  }
}

async function parseStreamChunks(
  buffer: string,
  state: StreamState,
  callbacks: StreamCallbacks
): Promise<string> {
  const parts = buffer.split('\n\n');
  const remaining = parts.pop() || '';

  for (const part of parts) {
    parseDataLine(part, state, callbacks);
  }

  return remaining;
}

async function processChunk(
  value: Uint8Array,
  decoder: globalThis.TextDecoder,
  buffer: string,
  state: StreamState,
  callbacks: StreamCallbacks
): Promise<string> {
  const newBuffer = buffer + decoder.decode(value, { stream: true });

  return await parseStreamChunks(newBuffer, state, callbacks);
}

type StreamReader = globalThis.ReadableStreamDefaultReader<Uint8Array>;

async function readStream(
  reader: StreamReader,
  decoder: globalThis.TextDecoder,
  state: StreamState,
  callbacks: StreamCallbacks
) {
  let buffer = '';

  while (true) {
    const result = await reader.read();

    if (result.done) break;
    buffer = await processChunk(result.value, decoder, buffer, state, callbacks);
  }
}

function createStreamState(projectId: string | undefined): StreamState {
  return {
    userMessageId: '',
    resolvedProjectId: projectId,
  };
}

function getStreamUrl(baseUrl: string, endpoint: string): string {
  return `${baseUrl.replace(/\/$/, '')}${endpoint}`;
}

async function initiateFetchStream(
  url: string,
  token: string | null,
  body: Record<string, unknown>,
  controller: globalThis.AbortController
): Promise<globalThis.Response> {
  return await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal: controller.signal,
  });
}

function parsedResponseDetail(errorBody: { detail?: unknown }): string | null {
  const detail = errorBody.detail;

  if (typeof detail === 'string' && detail.trim()) return detail;
  if (!detail || typeof detail !== 'object') return null;

  const message = (detail as { message?: unknown }).message;

  return typeof message === 'string' && message.trim() ? message : null;
}

/** The quota details of a "queued for your plan's hourly quota" answer, if that is what this is. */
async function quotaQueuedResponse(response: globalThis.Response): Promise<QuotaQueueInfo | null> {
  try {
    return quotaQueueInfo(await response.clone().json());
  } catch {
    return null;
  }
}

/** A queued-for-quota answer goes to the queue; anything else is an error. */
async function reportFailedStart(response: globalThis.Response, callbacks: StreamCallbacks): Promise<void> {
  const quota = response.status === 429 ? await quotaQueuedResponse(response) : null;

  if (quota && callbacks.onQuotaQueued) {
    callbacks.onQuotaQueued(quota);

    return;
  }
  callbacks.onError(await responseErrorMessage(response));
}

async function responseErrorMessage(response: globalThis.Response): Promise<string> {
  return errorMessage(
    await responseError(response),
    'We couldn’t start the response. Please try again.',
  );
}

async function executeStream(
  url: string,
  token: string | null,
  body: Record<string, unknown>,
  controller: globalThis.AbortController,
  state: StreamState,
  callbacks: StreamCallbacks
): Promise<void> {
  const response = await initiateFetchStream(url, token, body, controller);

  if (!response.ok || !response.body) {
    await reportFailedStart(response, callbacks);

    return;
  }

  await readStream(response.body.getReader(), new window.TextDecoder(), state, callbacks);
  callbacks.onStreamEnd?.();
}

const handleStreamError = (err: unknown, callbacks: { onError: (msg: string) => void }) => {
  if (!(err instanceof window.DOMException && err.name === 'AbortError')) {
    callbacks.onError(err instanceof Error ? err.message : 'Stream error');
  }
};

const createStreamController = () => new window.AbortController();

const getBaseUrl = () => axiosInstance.defaults.baseURL || '';

const buildStreamParams = (endpoint: string, projectId: string | undefined) => ({
  url: getStreamUrl(getBaseUrl(), endpoint),
  state: createStreamState(projectId),
  token: tokenService.getAccessToken(),
});

type StreamBody = Record<string, unknown>;
type AC = globalThis.AbortController;

const doExecuteStream = async (
  ep: string,
  body: StreamBody,
  pid: string | undefined,
  cbs: StreamCallbacks,
  ctrl: AC
) => {
  const { url, state, token } = buildStreamParams(ep, pid);

  await executeStream(url, token, body, ctrl, state, cbs);
};

async function runStreamRequest(
  ep: string,
  body: StreamBody,
  pid: string | undefined,
  cbs: StreamCallbacks,
  onController?: (controller: AC) => void,
): Promise<AC> {
  const ctrl = createStreamController();

  onController?.(ctrl);
  try {
    await doExecuteStream(ep, body, pid, cbs, ctrl);
  } catch (e) {
    handleStreamError(e, cbs);
  }

  return ctrl;
}

export const chatService = {
  /**
   * Send a new chat message
   */
  async sendMessage(
    content: string,
    author: string = 'user',
    projectId?: string,
    mediaUrls?: string[],
    isPublic?: boolean,
  ): Promise<ChatMessage> {
    const response = await axiosInstance.post<ChatMessage>('/api/v1/chat/messages', {
      content,
      author,
      project_id: projectId,
      ...(mediaUrls?.length ? { media_urls: mediaUrls } : {}),
      ...(isPublic !== undefined ? { is_public: isPublic } : {}),
    });

    return response.data;
  },

  /**
   * Get all messages for the current user
   */
  async getMessages(
    limit: number = 50,
    offset: number = 0,
    options: MessageListOptions = {},
  ): Promise<MessageListResponse> {
    const response = await axiosInstance.get<MessageListResponse>('/api/v1/chat/messages', {
      params: {
        limit,
        offset,
        ...(options.projectId ? { project_id: options.projectId } : {}),
        ...(options.sessionId ? { session_id: options.sessionId } : {}),
      },
    });

    return response.data;
  },

  /**
   * Get a specific message by ID
   */
  async getMessage(messageId: string): Promise<ChatMessage> {
    const response = await axiosInstance.get<ChatMessage>(`/api/v1/chat/messages/${messageId}`);

    return response.data;
  },

  /**
   * Delete a message by ID. Returns a snapshot so the client can Redo.
   */
  async deleteMessage(messageId: string): Promise<{
    deleted: boolean;
    snapshot?: { type: 'chat_message'; message: Record<string, unknown> } | null;
  }> {
    const response = await axiosInstance.delete(`/api/v1/chat/messages/${messageId}`);

    return response.data || { deleted: true };
  },

  /**
   * Restore a previously deleted chat message from its snapshot.
   */
  async restoreMessage(message: Record<string, unknown>): Promise<void> {
    await axiosInstance.post('/api/v1/chat/messages/restore', { message });
  },

  /**
   * Send a message and get AI response
   */
  async sendMessageWithAI(
    content: string,
    model: string = DEFAULT_CHAT_MODEL,
    projectId?: string
  ): Promise<AIMessageResponse> {
    const response = await axiosInstance.post<AIMessageResponse>('/api/v1/chat/messages/ai', {
      content,
      model,
      project_id: projectId,
    });

    return response.data;
  },

  /**
   * Send a message and stream AI response
   */
  async sendMessageWithAIStream(
    content: string,
    model: string = DEFAULT_CHAT_MODEL,
    projectId: string | undefined,
    callbacks: StreamCallbacks,
    mediaUrls?: string[],
    planAuto: boolean = false,
    sessionId?: string | null,
    mediaMetadata?: Record<string, unknown>[],
    promptScope?: Record<string, unknown> | null,
    selectedDesignElement?: SelectedDesignElement | null,
    onController?: (controller: globalThis.AbortController) => void,
  ): Promise<globalThis.AbortController> {
    return runStreamRequest(
      '/api/v1/chat/messages/ai/stream',
      {
        content,
        model,
        project_id: projectId,
        session_id: sessionId || undefined,
        media_urls: mediaUrls || undefined,
        media_metadata: mediaMetadata || undefined,
        prompt_scope: promptScope || undefined,
        selected_design_element: selectedDesignElement || undefined,
        plan_auto: planAuto,
      },
      projectId,
      callbacks,
      onController,
    );
  },

  /**
   * Trigger AI response for existing messages
   */
  async triggerAIResponse(
    projectId: string,
    model: string = DEFAULT_CHAT_MODEL,
    sessionId?: string | null,
  ): Promise<void> {
    await axiosInstance.post('/api/v1/chat/messages/ai/trigger', {
      project_id: projectId,
      session_id: sessionId || undefined,
      model,
    });
  },

  /**
   * Trigger AI response stream for existing messages
   */
  async triggerAIResponseStream(
    projectId: string,
    model: string = DEFAULT_CHAT_MODEL,
    callbacks: Omit<StreamCallbacks, 'onUserMessageId'>,
    sessionId?: string | null,
    onController?: (controller: globalThis.AbortController) => void,
    initialProjectBootstrap: boolean = false,
  ): Promise<globalThis.AbortController> {
    return runStreamRequest(
      '/api/v1/chat/messages/ai/stream-trigger',
      {
        project_id: projectId,
        session_id: sessionId || undefined,
        model,
        initial_project_bootstrap: initialProjectBootstrap,
      },
      projectId,
      callbacks as StreamCallbacks,
      onController,
    );
  },

  /**
   * Manually trigger the design cycle for a project
   */
  async triggerDesign(projectId: string): Promise<void> {
    await axiosInstance.post('/api/v1/chat/trigger-design', {
      project_id: projectId,
    });
  },

  /**
   * Manually trigger specific agents for a project
   */
  async triggerAgents(
    projectId: string,
    agents: string[],
    messageId: string = 'manual-trigger',
    sessionId?: string | null,
    promptScope?: Record<string, unknown> | null,
    planningActionInvocationId?: string,
  ): Promise<TriggerAgentsResponse> {
    const response = await axiosInstance.post<TriggerAgentsResponse>('/api/v1/chat/trigger-agents', {
      project_id: projectId,
      session_id: sessionId || undefined,
      agents,
      message_id: messageId,
      prompt_scope: promptScope || undefined,
      planning_action_invocation_id: planningActionInvocationId || undefined,
    });

    return response.data;
  },

  /** Whether Extreme landing design is unlocked for this user and its per-section price. */
  async getExtremeDesignInfo(): Promise<ExtremeDesignInfo> {
    const response = await axiosInstance.get<ExtremeDesignInfo>('/api/v1/chat/extreme-design');

    return response.data;
  },

  /**
   * Resume the planning pipeline after a review gate.
   *
   * The backend still verifies the stored project state before doing work, but
   * sending the active gate phase lets all_pages/first_page Continue clicks map
   * cleanly to their intended next step.
   */
  async resumeDesign(
    projectId: string,
    phase: 'user_flow' | 'first_page' | 'all_pages' | 'review_build' | 'auto' = 'auto',
    sessionId?: string | null,
    planningActionInvocationId?: string,
    designTier?: DesignTier,
  ): Promise<void> {
    const response = await axiosInstance.post('/api/v1/chat/resume-design', {
      project_id: projectId,
      phase,
      session_id: sessionId || undefined,
      ...(planningActionInvocationId ? { planning_action_invocation_id: planningActionInvocationId } : {}),
      ...(designTier ? { design_tier: designTier } : {}),
    }).catch((error: unknown) => {
      if (normalizeApiError(error).status === 402) signalInsufficientCredits();

      throw error;
    });

    if (response.data?.status === 'insufficient_credits') {
      signalInsufficientCredits(getCreditShortfall(response.data));
      throw new Error('Insufficient credits. Add credits and retry to resume the remaining work.');
    }
  },

  /**
   * Approve a developer-mode plan so the agent can start applying changes.
   */
  async approveDeveloperPlan(
    projectId: string,
    sessionId: string,
    planId: string,
  ): Promise<void> {
    await axiosInstance.post(
      `/api/v1/projects/${encodeURIComponent(projectId)}/developer-mode/plans/${encodeURIComponent(planId)}/approve`,
      { session_id: sessionId },
    );
  },

  /**
   * Deny a developer-mode plan and stop the proposed implementation.
   */
  async denyDeveloperPlan(
    projectId: string,
    sessionId: string,
    planId: string,
  ): Promise<void> {
    await axiosInstance.post(
      `/api/v1/projects/${encodeURIComponent(projectId)}/developer-mode/plans/${encodeURIComponent(planId)}/deny`,
      { session_id: sessionId },
    );
  },

  /**
   * Request a revision of a developer-mode plan with new instructions.
   */
  async reviseDeveloperPlan(
    projectId: string,
    sessionId: string,
    planId: string,
    instructions: string,
  ): Promise<void> {
    await axiosInstance.post(
      `/api/v1/projects/${encodeURIComponent(projectId)}/developer-mode/plans/${encodeURIComponent(planId)}/revise`,
      { session_id: sessionId, instructions: instructions.trim() },
    );
  },

  /**
   * Upload media files for chat messages
   */
  async uploadMedia(
    files: File[],
    projectId: string,
    sessionId?: string | null,
    onProgress?: (percent: number) => void,
    signal?: globalThis.AbortSignal,
  ): Promise<string[]> {
    const formData = new FormData();

    files.forEach((file) => formData.append('files', file));

    const baseUrl = axiosInstance.defaults.baseURL || '';
    const sessionParam = sessionId ? `&session_id=${encodeURIComponent(sessionId)}` : '';
    const url = `${baseUrl}/api/v1/chat/media/upload?project_id=${projectId}${sessionParam}`;
    const token = tokenService.getAccessToken();

    return _executeMediaUpload(url, formData, token, onProgress, signal);
  },
};

function _executeMediaUpload(
  url: string,
  formData: FormData,
  token: string | null,
  onProgress?: (percent: number) => void,
  signal?: globalThis.AbortSignal,
): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const xhr = new window.XMLHttpRequest();

    xhr.open('POST', url);
    _configureMediaUpload(xhr, token, onProgress, signal, resolve, reject);
    xhr.send(formData);
  });
}

function _configureMediaUpload(
  xhr: globalThis.XMLHttpRequest,
  token: string | null,
  onProgress: ((percent: number) => void) | undefined,
  signal: globalThis.AbortSignal | undefined,
  resolve: (urls: string[]) => void,
  reject: (err: Error) => void,
): void {
  if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  xhr.upload.onprogress = (event) => {
    if (event.lengthComputable && onProgress) {
      onProgress(Math.round((event.loaded / event.total) * 100));
    }
  };
  xhr.onload = () => _handleUploadResponse(xhr, resolve, reject);
  xhr.onerror = () => reject(new Error('Upload failed'));
  signal?.addEventListener('abort', () => {
    xhr.abort();
    reject(new globalThis.DOMException('Upload cancelled', 'AbortError'));
  });
}

function _handleUploadResponse(
  xhr: globalThis.XMLHttpRequest,
  resolve: (urls: string[]) => void,
  reject: (err: Error) => void
) {
  if (xhr.status >= 200 && xhr.status < 300) {
    resolve(JSON.parse(xhr.responseText).urls || []);
  } else {
    reject(new Error(`Upload failed: ${xhr.statusText}`));
  }
}
